import type { ITenantDestination } from '@/entities/tenant-destination'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDefaultTourSettingQuery, useUpdateDefaultTourMutation } from '@/entities/tenant'
import { useTenantDestinationsQuery } from '@/entities/tenant-destination'
import { AdminScopeType } from '@/shared/constants/admin-scope'
import { useAdminScope } from '@/shared/hooks/use-admin-scope'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import { ConfirmDialog } from '@/shared/ui/confirm-dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'

interface DefaultTourControlProps {
  tenantId: string
}

type DefaultTourAction = 'enable' | 'disable' | null

function getDestinationLabel(
  destination: Pick<ITenantDestination, 'id' | 'destinationId' | 'isActive' | 'localizedData'>,
) {
  const localizedValue = Object.values(destination.localizedData ?? {}).find(
    (value) => Boolean(value.name) || Boolean(value.title),
  )
  return (
    localizedValue?.name ?? localizedValue?.title ?? destination.destinationId ?? destination.id
  )
}

export function DefaultTourControl({ tenantId }: DefaultTourControlProps) {
  const { t } = useTranslation()
  const { scopeType } = useAdminScope()
  const mutation = useUpdateDefaultTourMutation()
  const [selectedDestinationId, setSelectedDestinationId] = useState('')
  const [pendingAction, setPendingAction] = useState<DefaultTourAction>(null)

  const scopedTenantId = scopeType === AdminScopeType.Super ? tenantId : undefined
  const defaultTourSetting = useDefaultTourSettingQuery(scopedTenantId)

  const defaultDestinationId = defaultTourSetting.data?.data.destinationId
  const [appliedDefaultId, setAppliedDefaultId] = useState<string | undefined>(undefined)
  if (defaultDestinationId && defaultDestinationId !== appliedDefaultId) {
    setAppliedDefaultId(defaultDestinationId)
    setSelectedDestinationId(defaultDestinationId)
  }

  const tenantDestinations = useTenantDestinationsQuery({
    current: 1,
    pageSize: 100,
    isActive: true,
    ...(scopedTenantId ? { tenantId: scopedTenantId } : {}),
  })
  const activeDestinations = useMemo(
    () =>
      (tenantDestinations.data?.data.data ?? []).filter(
        (destination): destination is ITenantDestination & { destinationId: string } =>
          destination.isActive && Boolean(destination.destinationId),
      ),
    [tenantDestinations.data?.data.data],
  )

  const isPending = mutation.isPending

  function requestAction(action: Exclude<DefaultTourAction, null>) {
    if (!selectedDestinationId) {
      showToast.error(t('defaultTour.selectRequired'))
      return
    }
    setPendingAction(action)
  }

  async function confirmAction() {
    if (!pendingAction || !selectedDestinationId) {
      return
    }

    try {
      await mutation.mutateAsync({
        ...(scopedTenantId ? { tenantId: scopedTenantId } : {}),
        enabled: pendingAction === 'enable',
        destinationId: selectedDestinationId,
      })
      showToast.success(
        t(pendingAction === 'enable' ? 'defaultTour.enabled' : 'defaultTour.disabled'),
      )
      setPendingAction(null)
    } catch (error) {
      showToast.error(getApiErrorText(error) ?? t('common.operationFailed'))
    }
  }

  return (
    <section className="mb-4 rounded-lg border border-zinc-200 bg-zinc-50 p-4">
      <h4 className="text-sm font-semibold text-zinc-900">{t('defaultTour.title')}</h4>
      <p className="mt-1 text-xs text-zinc-500">{t('defaultTour.description')}</p>

      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="min-w-0 flex-1">
          <label
            className="mb-1.5 block text-xs font-medium text-zinc-700"
            htmlFor="default-tour-destination"
          >
            {t('defaultTour.destination')}
          </label>
          <Select
            value={selectedDestinationId}
            onValueChange={setSelectedDestinationId}
            disabled={isPending || tenantDestinations.isLoading}
          >
            <SelectTrigger id="default-tour-destination">
              <SelectValue placeholder={t('defaultTour.selectDestination')} />
            </SelectTrigger>
            <SelectContent>
              {activeDestinations.map((destination) => (
                <SelectItem key={destination.id} value={destination.destinationId}>
                  {getDestinationLabel(destination)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-2">
          <Button
            disabled={isPending || !selectedDestinationId}
            onClick={() => requestAction('enable')}
          >
            {t('defaultTour.enable')}
          </Button>
          <Button
            disabled={isPending || !selectedDestinationId}
            onClick={() => requestAction('disable')}
            variant="outline"
          >
            {t('defaultTour.disable')}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={pendingAction !== null}
        title={t(
          pendingAction === 'enable'
            ? 'defaultTour.enableConfirmTitle'
            : 'defaultTour.disableConfirmTitle',
        )}
        description={t(
          pendingAction === 'enable'
            ? 'defaultTour.enableConfirmDescription'
            : 'defaultTour.disableConfirmDescription',
        )}
        confirmLabel={t('common.confirm')}
        cancelLabel={t('common.cancel')}
        loading={isPending}
        onCancel={() => setPendingAction(null)}
        onConfirm={() => void confirmAction()}
      />
    </section>
  )
}
